import {type ReactNode} from 'react'
import {View} from 'react-native'

import {atoms as a, useBreakpoints, useGutters} from '#/alf'
import * as Grid from '#/components/Grid'

/**
 * One cell of a work grid: a piece of work, or the way to add one.
 *
 * The cell says only *work* or *add*, and not which kind of work it is: a grid
 * holds one kind, so the card to draw is the tab's question and not the cell's. A
 * cell that carried the kind as well would be two answers to one question, free to
 * disagree.
 *
 * It carries its own key because the two are not derived the same way — work is
 * keyed by its id or its address, the way in by nothing at all — and the grid has to
 * hand React a stable key per cell either way.
 */
export type WorkCell<T> =
  | {kind: 'add'; key: string}
  | {kind: 'work'; key: string; item: T}

/** One row of a work grid: its cells. */
export interface WorkRow<T> {
  cells: WorkCell<T>[]
}

/** How many cards a work grid draws per row at this width. */
export function workColumns(gtMobile: boolean): number {
  return gtMobile ? 3 : 2
}

/**
 * The grid's cells, and its rows.
 *
 * The way in comes first and the work after it, and the columns are decided by the
 * caller rather than per row, so the last row is short rather than wrong. Every cell
 * fills the grid: work with no post to play keeps the card's shape and says what it
 * is, so the grid stays a grid and no cell has to be given the whole width to stay
 * readable.
 *
 * The way in is a cell of its own, and the caller says whether it is there at all —
 * a classroom shows it only to the people who can post, and only once the room holds
 * work of that kind, which is the pair of conditions the button above the grid used
 * to have. A profile shows it on the creator's own page, on the same terms.
 */
export function workRows<T>(
  items: T[],
  {
    columns,
    keyOf,
    withAdd,
  }: {
    columns: number
    keyOf: (item: T) => string
    /** Whether the grid's first cell is the way to add work. */
    withAdd: boolean
  },
): WorkRow<T>[] {
  const cells: WorkCell<T>[] = [
    ...(withAdd ? [{kind: 'add', key: 'add'} as const] : []),
    ...items.map(item => ({kind: 'work', key: keyOf(item), item}) as const),
  ]

  const rows: WorkRow<T>[] = []
  for (const cell of cells) {
    const last = rows[rows.length - 1]
    if (!last || last.cells.length === columns) {
      rows.push({cells: [cell]})
    } else {
      last.cells.push(cell)
    }
  }
  return rows
}

/** A row's key, for the list that draws the rows. */
export function workRowKey<T>(row: WorkRow<T>): string {
  return row.cells.map(cell => cell.key).join('-')
}

/**
 * One row of a work grid, whatever the grid is a grid of.
 *
 * The shape is the app's own, not any one screen's invention: the same gutters, the
 * same columns, the same cells the app draws that work with anywhere else. What the
 * row does *not* know is what kind of work it is holding or what to do about work
 * with no post behind it — those are the caller's, handed in as the two render
 * functions, which is what lets a classroom's grid and a profile's grid be one
 * component rather than two that have to be kept in step by hand.
 */
export function WorkGridRow<T>({
  cells,
  renderWork,
  renderAdd,
}: {
  cells: WorkCell<T>[]
  renderWork: (item: T) => ReactNode
  renderAdd: () => ReactNode
}) {
  const gutters = useGutters(['base', 'base', 0, 'base'])
  const {gtMobile} = useBreakpoints()

  return (
    <View style={[gutters]}>
      <View style={[a.flex_row, a.gap_sm]}>
        <Grid.Row gap={a.gap_sm.gap}>
          {cells.map(cell => (
            <Grid.Col key={cell.key} width={gtMobile ? 1 / 3 : 1 / 2}>
              {cell.kind === 'add' ? renderAdd() : renderWork(cell.item)}
            </Grid.Col>
          ))}
        </Grid.Row>
      </View>
    </View>
  )
}
